// The insight pages' shared range + "vs previous" toggle, kept in the query
// string so a link or reload keeps the view: ?days=7|30|90&compare=0.

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ADMIN_RANGES, type AdminRangeDays } from "@paceroo/domain/views/admin";

export interface RangeState {
  days: AdminRangeDays;
  compare: boolean;
  setDays: (days: AdminRangeDays) => void;
  setCompare: (compare: boolean) => void;
}

export function useRange(): RangeState {
  const [params, setParams] = useSearchParams();
  const asked = Number(params.get("days"));
  const days = ((ADMIN_RANGES as readonly number[]).includes(asked) ? asked : 30) as AdminRangeDays;
  const compare = params.get("compare") !== "0";
  const update = useCallback(
    (key: string, value: string | null) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          if (value === null) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  return {
    days,
    compare,
    setDays: (d) => update("days", d === 30 ? null : String(d)),
    setCompare: (c) => update("compare", c ? null : "0"),
  };
}

/** The query string the insight pages carry between each other. */
export function rangeQuery(range: Pick<RangeState, "days" | "compare">): string {
  const p = new URLSearchParams();
  if (range.days !== 30) p.set("days", String(range.days));
  if (!range.compare) p.set("compare", "0");
  const s = p.toString();
  return s ? `?${s}` : "";
}
